import React, { useCallback } from 'react';
import { useDropzone, FileRejection } from 'react-dropzone';
import { Box, Typography } from '@mui/material';
import { CloudUpload as CloudUploadIcon } from '@mui/icons-material';

const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10 MB

const ACCEPTED_MIME_TYPES: Record<string, string[]> = {
  'image/jpeg': [],
  'image/png': [],
  'image/webp': [],
};

export interface BulkDropZoneProps {
  onFilesAccepted: (files: File[]) => void;
  onFilesRejected: (rejections: { file: File; reason: string }[]) => void;
  disabled: boolean;
}

/**
 * BulkDropZone — A multi-file drag-and-drop zone for the admin bulk upload page.
 *
 * Wraps `react-dropzone` configured for multiple image files (JPEG, PNG, WebP)
 * with a 10 MB per-file size limit. Accepted files are forwarded via
 * `onFilesAccepted`; rejected files are forwarded via `onFilesRejected` with
 * human-readable reason strings.
 */
const BulkDropZone: React.FC<BulkDropZoneProps> = ({
  onFilesAccepted,
  onFilesRejected,
  disabled,
}) => {
  const handleDrop = useCallback(
    (accepted: File[], rejected: FileRejection[]) => {
      if (accepted.length > 0) {
        onFilesAccepted(accepted);
      }

      if (rejected.length > 0) {
        const mapped = rejected.map((rejection) => {
          const reasons = rejection.errors.map((err) => {
            if (err.code === 'file-too-large') {
              const sizeMB = (rejection.file.size / (1024 * 1024)).toFixed(1);
              return `File exceeds 10 MB limit (${sizeMB} MB)`;
            }
            if (err.code === 'file-invalid-type') {
              return `Unsupported file type: ${rejection.file.type || 'unknown'}`;
            }
            return err.message;
          });

          return {
            file: rejection.file,
            reason: reasons.join('; '),
          };
        });

        onFilesRejected(mapped);
      }
    },
    [onFilesAccepted, onFilesRejected],
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: handleDrop,
    accept: ACCEPTED_MIME_TYPES,
    maxSize: MAX_FILE_SIZE,
    multiple: true,
    disabled,
  });

  return (
    <Box
      {...getRootProps()}
      sx={{
        border: '2px dashed',
        borderColor: isDragActive
          ? 'primary.main'
          : disabled
            ? 'rgba(255, 255, 255, 0.2)'
            : 'rgba(255, 255, 255, 0.3)',
        borderRadius: 2,
        p: 4,
        textAlign: 'center',
        cursor: disabled ? 'not-allowed' : 'pointer',
        backgroundColor: isDragActive
          ? 'rgba(46, 125, 50, 0.08)'
          : 'rgba(255, 255, 255, 0.03)',
        opacity: disabled ? 0.5 : 1,
        transition: 'border-color 200ms ease, background-color 200ms ease',
        '&:hover': disabled
          ? {}
          : {
              borderColor: 'primary.light',
              backgroundColor: 'rgba(46, 125, 50, 0.05)',
            },
      }}
    >
      <input {...getInputProps()} />

      <CloudUploadIcon
        sx={{
          fontSize: 48,
          color: isDragActive ? 'primary.main' : 'text.secondary',
          mb: 1,
        }}
      />

      <Typography variant="h6" color="text.primary" gutterBottom>
        {isDragActive
          ? 'Drop images here...'
          : 'Drag & drop outfit images here'}
      </Typography>

      <Typography variant="body2" color="text.secondary">
        {disabled
          ? 'Upload in progress — file selection disabled'
          : 'or click to select files • JPEG, PNG, WebP • Max 10 MB each'}
      </Typography>
    </Box>
  );
};

export default BulkDropZone;
